import { useMemo, useState, useRef, useEffect, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { SortingState, RowSelectionState } from "@tanstack/react-table";
import type { DateRange } from "react-day-picker";
import { useDebounce } from "./useDebounce";
import { ordersApi, Order } from "@/lib/api";
import { getOrderListStatus } from "@/lib/orderStatus";

// Tipos estendidos e de paginação
export interface OrderExtended extends Order {
  financialStatus: "paid" | "partial" | "overdue" | "pending";
  overdueAmount: number;
  lastUpdate: string;
  deliveryType?: "anual" | "semestral" | "trimestral" | "mensal";
  responsibleUser?: string;
}

export interface PaginationState {
  pageIndex: number;
  pageSize: number;
}

export interface FiltersState {
  dateRange?: DateRange;
  status: string;
  client: string;
  minValue: string;
  maxValue: string;
  deliveryType: string;
  searchQuery: string;
  type: string;
}

const STORAGE_KEY = "orderManagement_filters";

const defaultFilters: FiltersState = {
  status: "all",
  client: "all",
  minValue: "",
  maxValue: "",
  deliveryType: "all",
  searchQuery: "",
  type: "all",
};

function loadFiltersFromStorage(): FiltersState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultFilters;
    const parsed = JSON.parse(raw);

    // Reconstruir dateRange convertendo ISO strings de volta para Date
    let dateRange: DateRange | undefined;
    if (parsed.dateRange) {
      dateRange = {
        from: parsed.dateRange.from ? new Date(parsed.dateRange.from) : undefined,
        to: parsed.dateRange.to ? new Date(parsed.dateRange.to) : undefined,
      };
    }

    return {
      ...defaultFilters,
      ...parsed,
      dateRange,
    };
  } catch {
    return defaultFilters;
  }
}

function saveFiltersToStorage(filters: FiltersState) {
  try {
    // Serializar dateRange convertendo Date para ISO string
    const toSave = {
      ...filters,
      dateRange: filters.dateRange
        ? {
            from: filters.dateRange.from?.toISOString() ?? null,
            to: filters.dateRange.to?.toISOString() ?? null,
          }
        : undefined,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
  } catch {
    // Silenciar erros de localStorage (ex: modo privado cheio)
  }
}

export const useOrderManagement = () => {
  const searchRef = useRef<HTMLInputElement>(null);

  const [filtersOpen, setFiltersOpen] = useState(true);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 15,
  });

  const [filters, setFilters] = useState<FiltersState>(loadFiltersFromStorage);

  // Sincronizar filtros com localStorage sempre que mudarem
  useEffect(() => {
    saveFiltersToStorage(filters);
  }, [filters]);

  const debouncedSearchQuery = useDebounce(filters.searchQuery, 300);

  // 1. Buscar todos os dados apenas uma vez
  const {
    data: allOrders = [],
    isLoading,
    error,
    refetch,
    isFetching,
  } = useQuery<Order[]>({
    queryKey: ["orders"],
    queryFn: async () => {
      const { data } = await ordersApi.getOrderHistory();
      return data || [];
    },
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: true,
  });

  // 2. Aplicar filtros, ordenação e paginação no lado do cliente
  const processedData = useMemo(() => {
    let filteredOrders: Order[] = [...allOrders];

    // Lógica de filtragem
    if (debouncedSearchQuery) {
      const query = debouncedSearchQuery.toLowerCase();
      filteredOrders = filteredOrders.filter(
        (order) =>
          order.orderNumber.toLowerCase().includes(query) ||
          order.clientName.toLowerCase().includes(query) ||
          order.clientCode.toLowerCase().includes(query) ||
          (order.clientCnpj && String(order.clientCnpj).toLowerCase().includes(query)) ||
          (order.customer?.clientCnpj && order.customer.clientCnpj.toLowerCase().includes(query)),
      );
    }
    if (filters.status && filters.status !== "all") {
      filteredOrders = filteredOrders.filter(
        (order) => getOrderListStatus(order) === filters.status,
      );
    }
    if (filters.client && filters.client !== "all") {
      filteredOrders = filteredOrders.filter(
        (order) => order.clientCode === filters.client,
      );
    }
    if (filters.dateRange?.from) {
      filteredOrders = filteredOrders.filter(
        (order) => new Date(order.createdAt) >= filters.dateRange!.from!,
      );
    }
    if (filters.dateRange?.to) {
      const toDate = new Date(filters.dateRange.to);
      toDate.setHours(23, 59, 59, 999); // Incluir todo o dia final
      filteredOrders = filteredOrders.filter(
        (order) => new Date(order.createdAt) <= toDate,
      );
    }
    if (filters.deliveryType && filters.deliveryType !== "all") {
      const deliveryTypeMap = {
        anual: "PED-A",
        semestral: "PED-S",
      };
      const prefix =
        deliveryTypeMap[filters.deliveryType as keyof typeof deliveryTypeMap];
      if (prefix) {
        filteredOrders = filteredOrders.filter((order) =>
          order.orderNumber.startsWith(prefix),
        );
      }
    }

    if (filters.type && filters.type !== "all") {
      filteredOrders = filteredOrders.filter((order) => {
        const shortCode = `${order.cycleCode}`.slice(-2);
        const orderNumber = order.orderNumber;

        // Determinar o tipo do pedido seguindo a mesma ordem de prioridade do componente StatusOrdersCompleted
        let orderType = "";
        if (shortCode === "00" || orderNumber.includes("SIM")) {
          orderType = "simulado";
        } else if (shortCode === "02") {
          orderType = "segundo_semestre";
        } else if (orderNumber.includes("PED-S")) {
          orderType = "semestral";
        } else if (shortCode === "01" || orderNumber.includes("PED-A")) {
          orderType = "anual";
        }

        return orderType === filters.type;
      });
    }

    // Lógica de ordenação
    if (sorting.length > 0) {
      const { id, desc } = sorting[0];
      filteredOrders.sort((a, b) => {
        const valA = a[id as keyof Order] ?? "";
        const valB = b[id as keyof Order] ?? "";
        if (valA < valB) return desc ? 1 : -1;
        if (valA > valB) return desc ? -1 : 1;
        return 0;
      });
    }

    const totalRows = filteredOrders.length;
    const totalPages = Math.ceil(totalRows / pagination.pageSize);

    // Lógica de paginação
    const start = pagination.pageIndex * pagination.pageSize;
    const end = start + pagination.pageSize;
    const paginatedData = filteredOrders.slice(start, end);

    return {
      paginatedData,
      totalRows,
      totalPages,
    };
  }, [allOrders, filters, debouncedSearchQuery, sorting, pagination]);

  const handleClearFilters = () => {
    setFilters({ ...defaultFilters, dateRange: undefined });
    localStorage.removeItem(STORAGE_KEY);
    setPagination((p) => ({ ...p, pageIndex: 0 }));
    setSorting([]);
  };

  // Garante que a página seja resetada se os filtros mudarem e a página atual ficar vazia
  useMemo(() => {
    if (
      pagination.pageIndex > 0 &&
      pagination.pageIndex >= processedData.totalPages
    ) {
      setPagination((p) => ({ ...p, pageIndex: 0 }));
    }
  }, [pagination.pageIndex, processedData.totalPages]);

  return {
    searchRef,
    filtersOpen,
    setFiltersOpen,
    allOrders,
    tableData: processedData.paginatedData,
    totalRows: processedData.totalRows,
    totalPages: processedData.totalPages,
    sorting,
    setSorting,
    rowSelection,
    setRowSelection,
    pagination,
    setPagination,
    filters,
    setFilters,
    handleClearFilters,
    isLoading,
    isFetching, // Ainda pode ser útil para mostrar o carregamento inicial
    error,
    refetch,
  };
};
